/**
 * /well-testing/jobs — the job register (GET /well-testing/jobs,
 * fieldiq-api#1245, WT-2).
 *
 * Full width; a row opens the job on its own page. Filters (status, customer,
 * search) live in the URL so Back returns to the list as it was. Every filter
 * is server-side. A field hand's list is the API's — only the jobs they are
 * crewed on — and New job is offered to the dispatch roles only.
 *
 * The counts are a tally of the unfiltered list (no endpoint returns one).
 * Status chips come from the catalog (GET /meta), so a status label is the
 * API's.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarClock, ClipboardList, Flame, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWtJobs, useWtMeta } from '../../hooks/useWellTesting';
import { queryLoadFailure } from '../../lib/load-failure';
import { canDispatchWt, wtJobStatusSkin } from '../../lib/well-testing';
import type { WtJob } from '../../types/well-testing';
import { useOpenRecord } from '../downhole/recordPage';
import { JobCard, jobColumns } from './JobBits';
import { WT_EYEBROW, WT_INPUT, WtChip, WtChipRow, WtEmpty, WtPanel, WtRegister } from './WtBits';

const ON_SITE = new Set(['rigging_up', 'flowing', 'rigging_down']);

export default function WtJobsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const catalog = useWtMeta().data?.data ?? null;
  const filters = useMemo(() => ({ status, customerId, q: qParam }), [status, customerId, qParam]);
  const filtered = !!(status || customerId || qParam);
  const listQuery = useWtJobs(filters);
  const allQuery = useWtJobs();
  const jobs = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (j: WtJob) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canDispatchWt(user?.role);

  const customers = useMemo(() => {
    const seen = new Map<string, string>();
    (all ?? []).forEach((j) => seen.set(j.customerId, j.customerName ?? '—'));
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const statuses = catalog?.jobStatuses ?? Object.entries({
    draft: 'Draft', scheduled: 'Scheduled', rigging_up: 'Rigging up', flowing: 'Flowing',
    rigging_down: 'Rigging down', completed: 'Completed', cancelled: 'Cancelled',
  }).map(([value, label]) => ({ value, label }));

  return (
    <div data-testid="wt-jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title="Jobs"
        description="Flowback and well-test jobs — crew, packages, rent-ins and shift logs."
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/well-testing/jobs/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New job
            </button>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Jobs', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Scheduled', tone: 'info', loading: countsLoading, value: countOf((j) => j.status === 'scheduled') },
          { icon: <Flame className="size-4" aria-hidden="true" />, label: 'On site', sub: 'Rig up to rig down', tone: 'accent', loading: countsLoading, value: countOf((j) => ON_SITE.has(j.status)) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WtChipRow label="Filter by status">
            <WtChip label="All" active={!status} onClick={() => setParam('status', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {statuses.map((s) => (
              <WtChip
                key={s.value}
                label={s.label}
                active={status === s.value}
                onClick={() => setParam('status', s.value)}
                count={countsLoading ? undefined : countOf((j) => j.status === s.value)}
              />
            ))}
          </WtChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_16rem]">
            <label className="relative block">
              <span className="sr-only">Search job, well, customer, PO or AFE</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search job, well, customer, PO or AFE"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={WT_INPUT} aria-label="Filter by customer" value={customerId} onChange={(e) => setParam('customerId', e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <WtPanel
            eyebrow="Job register"
            title="Well testing jobs"
            testId="wt-job-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && jobs.length > 0
                ? filtered
                  ? `Showing ${jobs.length} of ${countOf(() => true)} jobs`
                  : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <WtRegister
              query={listQuery}
              rows={jobs}
              columns={jobColumns()}
              rail={(j) => wtJobStatusSkin(j.status).rail}
              onOpen={(j) => openRecord(`/well-testing/jobs/${j.id}`)}
              subject="jobs"
              testId="wt-job"
              // Fits the panel at 1280 (about 970px) — see WellsScreen.
              minWidth="min-w-[900px]"
              empty={filtered ? <WtEmpty>No job matches these filters.</WtEmpty> : <WtEmpty>There are no well testing jobs yet.</WtEmpty>}
              card={(j) => <JobCard job={j} />}
            />
          </WtPanel>
        </div>
      </section>
    </div>
  );
}
